import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from 'src/auth';

import { NO_PROJECT } from '../constants';

import { readFrames } from './readFrames';
import type {
  BuildStatus,
  Chat,
  ChatBranding,
  ChatCredentials,
  ChatSummary,
  CredentialLink,
  ReplyRating,
  ReplyRatingDetail,
  StreamFrame,
} from './types';

/** A failed call to Andi's API. `detail` is the server's text, for the console only: it is never shown, except
 *  for a refusal's reason (see `refusalReason`). */
export class AndiApiError extends Error {
  constructor(readonly status: number, readonly detail: string) {
    super(`Andi request failed (${status})`);
    this.name = 'AndiApiError';
  }
}

export const isNotFound = (error: unknown) => error instanceof AndiApiError && error.status === 404;

/** The call was refused outright (no project, not signed in, not allowed, or no such chat): retrying won't
 *  help. */
export const isRefused = (error: unknown) => (
  error instanceof AndiApiError && [400, 401, 403, 404].includes(error.status)
);

/** Why the server refused a request (a 400's `error`): a plain sentence written for the builder to read. */
export const refusalReason = (error: unknown): string | null => {
  if (!(error instanceof AndiApiError) || error.status !== 400) return null;
  try {
    const { error: reason } = JSON.parse(error.detail) as { error?: unknown };
    return typeof reason === 'string' && reason ? reason : null;
  } catch {
    return null;
  }
};

/** Where a call about no chat in particular goes (feedback, or dictation before a chat exists). */
const NO_CHAT = '-';

/**
 * Andi's API. Calls go to the Ampersand API, which checks the builder's session and project and forwards
 * them to Andi's service; the dashboard holds no key of its own. Who is signed in reaches Andi from the
 * Ampersand API too, never from the request's body.
 */
export class AndiApi {
  /** `baseUrl` is the project's root in Andi's API: chats live under it. */
  constructor(private readonly baseUrl: string, private readonly token: string | null) {}

  async listChats() {
    const { chats } = await this.json<{ chats?: ChatSummary[] }>('/chats');
    return chats ?? [];
  }

  async getChat(chatId: string) {
    const { chat } = await this.json<{ chat: Chat }>(`/chats/${chatId}?includeContents=true&includeSpec=true`);
    return chat;
  }

  /** Creates a chat with its opening message, which the server keeps (the first reply answers it) and names
   *  the chat from. */
  async startChat(message: string) {
    const { chat } = await this.json<{ chat: Chat }>('/chats', { method: 'POST', body: { message } });
    return chat;
  }

  async renameChat(chatId: string, title: string) {
    await this.send(`/chats/${chatId}`, { method: 'PATCH', body: { updateMask: 'title', title } });
  }

  async deleteChat(chatId: string) {
    await this.send(`/chats/${chatId}`, { method: 'DELETE' });
  }

  /** Sends a message and streams the reply. Without one, the reply answers the message the chat already holds
   *  (its opening message, or one whose reply was interrupted). The first frame names the stream. */
  async* streamReply(chatId: string, message: string | null, signal?: AbortSignal) {
    yield* this.frames(`/chats/${chatId}/streams`, {
      method: 'POST', body: message === null ? {} : { message }, signal,
    });
  }

  /** Follows a reply's stream: the frames so far, then the live ones until it ends. */
  async* resumeReply(chatId: string, streamId: string) {
    yield* this.frames(`/chats/${chatId}/streams/${streamId}:resume`);
  }

  async rateReply(chatId: string, index: number, rating: ReplyRating, detail?: ReplyRatingDetail) {
    await this.send(`/chats/${chatId}:feedback`, { method: 'POST', body: { index, rating, ...detail } });
  }

  /** Feedback about the whole chat or the product. Without a chat, it is sent as being about none. */
  async sendFeedback(chatId: string | null, text: string, includeChat: boolean) {
    await this.send(`/chats/${chatId || NO_CHAT}:report`, {
      method: 'POST',
      body: { kind: 'feedback', text, include_chat: includeChat && Boolean(chatId) },
    });
  }

  getBuildStatus(chatId: string) {
    return this.json<BuildStatus>(`/chats/${chatId}/build-status`);
  }

  /** The credentials the chat's build needs to test against a live account, and what's saved so far. */
  getChatCredentials(chatId: string) {
    return this.json<ChatCredentials>(`/chats/${chatId}/credentials`);
  }

  /** Saves credentials by field name. They merge into what's saved: a field left out keeps its value. */
  saveChatCredentials(chatId: string, values: Record<string, string>) {
    return this.json<ChatCredentials>(`/chats/${chatId}/credentials`, { method: 'PUT', body: { values } });
  }

  /** A link the builder's customer fills in with the credentials, its page branded as given. The chat's
   *  previous link stops working, except for a preview: it can't be submitted and leaves the chat's link as
   *  it is. */
  createCredentialLink(chatId: string, link: { ttlMinutes: number; preview?: boolean; branding: ChatBranding }) {
    return this.json<CredentialLink>(`/chats/${chatId}/credential-links`, { method: 'POST', body: link });
  }

  async transcribe(chatId: string | null, clip: Blob): Promise<string> {
    const form = new FormData();
    form.append('audio', clip, 'dictation');
    const { text } = await this.json<{ text?: string }>(`/chats/${chatId || NO_CHAT}:dictate`, { method: 'POST', form });
    return (text ?? '').trim();
  }

  private async json<T>(path: string, request?: AndiRequest): Promise<T> {
    const res = await this.send(path, request);
    return res.json() as Promise<T>;
  }

  private async* frames(path: string, request?: AndiRequest): AsyncGenerator<StreamFrame> {
    const res = await this.send(path, request);
    if (!res.body) throw new AndiApiError(res.status, 'the stream had no body');
    yield* readFrames(res.body);
  }

  private async send(path: string, { method = 'GET', body, form, signal }: AndiRequest = {}): Promise<Response> {
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    // A multipart body sets its own Content-Type, with the boundary.
    if (body) headers['Content-Type'] = 'application/json';
    const res = await fetch(`${this.baseUrl}${path}`, {
      method, headers, signal, body: form ?? (body ? JSON.stringify(body) : undefined),
    });
    if (!res.ok) throw new AndiApiError(res.status, await res.text().catch(() => res.statusText));
    return res;
  }
}

type AndiRequest = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: object;
  form?: FormData;
  signal?: AbortSignal;
};

/** The project's root in Andi's API, reached through the Ampersand API's route for Andi, which forwards the
 *  rest of the path as it is. */
const andiBaseUrl = (projectId: string) => {
  const api = (import.meta.env.VITE_AMP_API_URL ?? '').replace(/\/+$/, '');
  return `${api}/internal/v1/projects/${projectId}/andi/projects/${projectId}`;
};

/** Andi's API for `projectId`, signed in as the builder. Like `useAPI`, it returns a getter, so each call
 *  carries a fresh session token. */
export function useAndiAPIFor(projectId: string | undefined): () => Promise<AndiApi> {
  const { getToken } = useAuth();

  return useCallback(async () => {
    // Without a real project the call can only be refused, so refuse it here, as the server would, instead of
    // sending it (a chat's first message once went to /projects/_/andi and waited on 403s).
    if (!projectId || projectId === NO_PROJECT) {
      console.error('[andi] no project to call Andi with', { projectId });
      throw new AndiApiError(400, JSON.stringify({ error: 'Open a project first.' }));
    }
    return new AndiApi(andiBaseUrl(projectId), await getToken());
  }, [getToken, projectId]);
}

/** Andi's API for the current project, the one in the URL. */
export function useAndiAPI(): () => Promise<AndiApi> {
  const { projectId } = useParams();
  return useAndiAPIFor(projectId);
}
